import { UnitMode } from "~/domains/userPreferences/types/UnitMode";
import { SearchDisplayMode } from "~/features/search/models/models";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { ThunkAction } from "redux-thunk";
import type { AnyAction } from "redux";
import { apiUpdateUserPreferences } from "~/features/search/api/apiUpdateUserPreferences";
import { SELECT_SERIES_TREE_NODE, SERIES_TREE_BACK } from "~/features/search/redux/reducer/seriesTreeReducer";
import type { RootState } from "~/app/store";

export interface UserPreferencesSlice {
  unitMode: UnitMode;
  searchDisplayMode: SearchDisplayMode;
}

const initialState: UserPreferencesSlice = {
  unitMode: UnitMode.Metric,
  searchDisplayMode: SearchDisplayMode.LIST,
};

export const userPreferencesSlice = createSlice({
  name: "userPreferences",
  initialState,
  reducers: {
    updateUnitMode: (state, action: PayloadAction<UnitMode>) => {
      state.unitMode = action.payload;
    },
    updateSearchDisplayMode: (state, action: PayloadAction<SearchDisplayMode>) => {
      state.searchDisplayMode = action.payload;
    },
    hydrateUserPreferences: (state, action: PayloadAction<{ unit?: string; searchView?: string }>) => {
      state.unitMode = (action.payload.unit as UnitMode | undefined) ?? state.unitMode;
      state.searchDisplayMode = (action.payload.searchView as SearchDisplayMode | undefined) ?? state.searchDisplayMode;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(SELECT_SERIES_TREE_NODE, (state) => {
        state.searchDisplayMode = SearchDisplayMode.TABULAR;
      })
      .addCase(SERIES_TREE_BACK, (state) => {
        state.searchDisplayMode = SearchDisplayMode.SERIES_TREE;
      });
  },
});

const { updateUnitMode, updateSearchDisplayMode } = userPreferencesSlice.actions;
export const { hydrateUserPreferences } = userPreferencesSlice.actions;

export function changeUnitModeThunk(newMode: UnitMode): ThunkAction<void, RootState, unknown, AnyAction> {
  return (dispatch, getState) => {
    if (getState().userPreferences.unitMode === newMode) {
      return;
    }

    void apiUpdateUserPreferences({ unit: newMode }).then((response) => {
      if (response.ok) {
        dispatch(updateUnitMode(newMode));
      }
    });
  };
}

export function changeDisplayModeThunk(
  displayMode: SearchDisplayMode,
): ThunkAction<void, Pick<RootState, "userPreferences">, unknown, AnyAction> {
  return (dispatch, getState) => {
    if (getState().userPreferences.searchDisplayMode === displayMode) {
      return;
    }

    void apiUpdateUserPreferences({ view: displayMode }).then((response) => {
      if (response.ok) {
        dispatch(updateSearchDisplayMode(displayMode));
      }
    });
  };
}

export default userPreferencesSlice.reducer;
